/**
 * CastPilot Assistant chat engagement analytics.
 *
 * Events are captured directly via posthog-js so property names are stored in
 * PostHog exactly as they appear in this file (snake_case), matching the naming
 * used by the Python backend.  No intermediate serialisation layer is involved.
 */
import posthog from 'posthog-js';

import { CAST_PILOT_ASSISTANT } from '@cast/constants';

// ── Domain types ───────────────────────────────────────────────────────────────

/** How a question entered the chat. */
export type QuerySource = 'input' | 'suggestion' | 'predefined';

/** Why the panel was opened — for distinguishing intentional vs incidental opens. */
type OpenTrigger = 'launcher' | 'shortcut' | 'deep-link' | 'programmatic';

/** Why the panel closed — explicit close vs an inferred navigation away. */
export type CloseTrigger = 'close-button' | 'escape' | 'navigation';

/** Where a clicked answer link points — drives the "did the answer act" analysis. */
type LinkDestination = 'console-cluster' | 'console-other' | 'external';

// ── Event names ────────────────────────────────────────────────────────────────

export const OpsPilotChatEvent = {
  opened: `${CAST_PILOT_ASSISTANT}: Chat opened`,
  inputStarted: `${CAST_PILOT_ASSISTANT}: Chat input started`,
  closed: `${CAST_PILOT_ASSISTANT}: Chat closed`,
  querySubmitted: `${CAST_PILOT_ASSISTANT}: Query submitted`,
  queryCompleted: `${CAST_PILOT_ASSISTANT}: Query completed`,
  queryFailed: `${CAST_PILOT_ASSISTANT}: Query failed`,
  followUpClicked: `${CAST_PILOT_ASSISTANT}: Follow-up clicked`,
  followUpsDismissed: `${CAST_PILOT_ASSISTANT}: Follow-ups dismissed`,
  linkClicked: `${CAST_PILOT_ASSISTANT}: Answer link clicked`,
  answerCopied: `${CAST_PILOT_ASSISTANT}: Answer copied`,
  newChatStarted: `${CAST_PILOT_ASSISTANT}: New chat started`,
  fullScreenToggled: `${CAST_PILOT_ASSISTANT}: Full screen toggled`,
  historyOpened: `${CAST_PILOT_ASSISTANT}: History opened`,
  crashed: `${CAST_PILOT_ASSISTANT}: Chat crashed`,
} as const;

type EventKey = keyof typeof OpsPilotChatEvent;

// ── Shared prop shapes ─────────────────────────────────────────────────────────

/** Props present on every OpsPilot event. */
type BaseProps = {
  context_id?: string;
  organization_id?: string;
};

/**
 * Props for session-scoped events (open → input → close cycle).
 * `open_id` is a nanoid generated when the panel opens; it lets you join
 * the opened/inputStarted/closed trio without relying on session boundaries.
 */
type SessionProps = BaseProps & { open_id?: string };

// ── Per-event prop specs ───────────────────────────────────────────────────────

type PropsMap = {
  opened: SessionProps & { trigger: OpenTrigger; had_unread: boolean };
  inputStarted: SessionProps;
  closed: SessionProps & {
    trigger: CloseTrigger;
    questions_asked: number;
    did_type: boolean;
  };
  querySubmitted: BaseProps & {
    context_id: string;
    source: QuerySource;
    query_length: number;
    message_index: number;
  };
  queryCompleted: BaseProps & {
    context_id: string;
    latency_ms: number;
    message_index: number;
  };
  queryFailed: BaseProps & {
    context_id: string;
    latency_ms: number;
    aborted: boolean;
    /** Mirrors backend `error_type` taxonomy: 'abort' | 'http' | 'network' | 'unknown' */
    error_type?: string;
    /** Human-readable error message for PostHog ↔ Sentry correlation */
    error_message?: string;
    /** HTTP status code when the failure was an HTTP error */
    http_status?: number;
  };
  followUpClicked: BaseProps & { command: string; position: number };
  followUpsDismissed: BaseProps;
  linkClicked: BaseProps & { destination: LinkDestination; href: string };
  answerCopied: BaseProps;
  newChatStarted: BaseProps;
  fullScreenToggled: BaseProps & { is_full_screen: boolean };
  historyOpened: BaseProps;
  crashed: {
    error_name?: string;
    error_message?: string;
    error_type?: string;
  };
};

// ── Tracker factory ────────────────────────────────────────────────────────────

/**
 * Creates a typed tracker function for a given OpsPilot event.
 * When `withReferrer` is true, `referrer_url` is automatically attached so
 * entry-point events (open, first input, query submitted) capture the page
 * context without repetition at call sites.
 */
function makeTracker<K extends EventKey>(
  key: K,
  withReferrer = false
): (props: PropsMap[K]) => void {
  return (props) =>
    posthog.capture(OpsPilotChatEvent[key], {
      ...(withReferrer && { referrer_url: window.location.href }),
      ...props,
    });
}

// ── Public API ─────────────────────────────────────────────────────────────────

export const trackOpsPilot = {
  // Session lifecycle
  chatOpened: makeTracker('opened', true),
  chatInputStarted: makeTracker('inputStarted', true),
  chatClosed: makeTracker('closed'),
  // Query lifecycle
  querySubmitted: makeTracker('querySubmitted', true),
  queryCompleted: makeTracker('queryCompleted'),
  queryFailed: makeTracker('queryFailed'),
  // In-chat interactions
  followUpClicked: makeTracker('followUpClicked'),
  followUpsDismissed: makeTracker('followUpsDismissed'),
  answerLinkClicked: makeTracker('linkClicked'),
  answerCopied: makeTracker('answerCopied'),
  newChatStarted: makeTracker('newChatStarted'),
  fullScreenToggled: makeTracker('fullScreenToggled'),
  historyOpened: makeTracker('historyOpened'),
  // Errors
  chatCrashed: makeTracker('crashed'),
};

// ── Utilities ──────────────────────────────────────────────────────────────────

const isClusterPath = (path: string) => path.includes('/external-clusters/');

/**
 * Classify an answer hyperlink. OpsPilot synthesis deliberately embeds console
 * deep-links (e.g. `/external-clusters/<uuid>/dashboard`); a click on one means
 * the answer pushed the user into the product, which is the strongest signal
 * that the response was actionable.
 */
export const classifyLinkDestination = (href: string): LinkDestination => {
  try {
    const url = new URL(href, window.location.origin);
    const isConsole =
      url.hostname === window.location.hostname ||
      /(^|\.)cast\.ai$/.test(url.hostname);
    if (!isConsole) return 'external';
    return isClusterPath(url.pathname) ? 'console-cluster' : 'console-other';
  } catch {
    // Relative or malformed href — treat as an in-console link.
    return isClusterPath(href) ? 'console-cluster' : 'console-other';
  }
};
